import { useState } from 'react';
import { useStore } from '../../data/store';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { X, Instagram } from 'lucide-react';

export default function Gallery() {
  const gallery = useStore((s) => s.gallery) || [];
  const settings = useStore((s) => s.settings);
  const [filter, setFilter] = useState('ALLE');
  const [lightbox, setLightbox] = useState(null);
  const [ref, isVisible] = useScrollAnimation();

  const years = [...new Set(gallery.map((g) => g.year))].sort((a, b) => b - a);
  const filtered = filter === 'ALLE' ? gallery : gallery.filter((g) => g.year === parseInt(filter));
  const sorted = [...filtered].sort((a, b) => a.order - b.order);

  return (
    <section id="gallery" className="bg-bg-secondary section-padding min-h-[400px]">
      <div
        ref={ref}
        className={`max-w-7xl mx-auto transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <div className="text-center mb-10 md:mb-12">
          <h2 className="font-heading text-text-primary mb-4" style={{ fontSize: 'clamp(28px, 7vw, 56px)' }}>IMPRESSIONEN</h2>
          <p className="font-body text-text-muted text-base md:text-lg">So fühlt sich Rock'n'Rhein an.</p>
        </div>

        {years.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 md:mb-12">
            <button
              onClick={() => setFilter('ALLE')}
              className={`font-heading text-xs tracking-wider px-4 py-2 border transition-all min-h-[40px] ${
                filter === 'ALLE' ? 'bg-accent-red border-accent-red text-white' : 'border-divider text-text-muted hover:border-accent-red hover:text-accent-red'
              }`}
            >
              ALLE
            </button>
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setFilter(String(y))}
                className={`font-heading text-xs tracking-wider px-4 py-2 border transition-all min-h-[40px] ${
                  filter === String(y) ? 'bg-accent-red border-accent-red text-white' : 'border-divider text-text-muted hover:border-accent-red hover:text-accent-red'
                }`}
              >
                {y}
              </button>
            ))}
          </div>
        )}

        <div className="gallery-grid">
          {sorted.map((item) => (
            <div
              key={item.id}
              className="gallery-item break-inside-avoid cursor-pointer group relative overflow-hidden mb-2 sm:mb-3"
              onClick={() => setLightbox(item)}
            >
              <img
                src={item.url}
                alt={item.caption}
                className="gallery-img w-full object-cover transition-transform duration-300 group-hover:scale-105 block"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-end">
                <p className="text-white font-body text-sm p-4 opacity-0 group-hover:opacity-100 transition-opacity">
                  {item.caption}
                </p>
              </div>
            </div>
          ))}
        </div>

        {sorted.length === 0 && (
          <p className="text-text-muted text-center font-body py-12">Noch keine Bilder vorhanden.</p>
        )}

        <div className="text-center mt-12 md:mt-16">
          {settings?.instagram && (
            <a
              href={settings.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border-2 border-text-primary text-text-primary font-heading tracking-wider px-6 py-3 hover:bg-text-primary hover:text-bg-primary transition-all min-h-[44px]"
            >
              <Instagram size={20} />
              MEHR AUF INSTAGRAM
            </a>
          )}
        </div>
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)}>
          <button className="absolute top-6 right-6 text-white hover:text-accent-red transition-colors w-11 h-11 flex items-center justify-center" onClick={() => setLightbox(null)} aria-label="Schließen">
            <X size={32} />
          </button>
          <img src={lightbox.url} alt={lightbox.caption} className="max-w-full max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
          <p className="absolute bottom-6 left-0 right-0 text-center text-white font-body text-sm">{lightbox.caption}</p>
        </div>
      )}

      <style>{`
        .gallery-grid {
          columns: 1;
          column-gap: 8px;
        }
        @media (min-width: 480px) {
          .gallery-grid { columns: 2; }
        }
        @media (min-width: 1024px) {
          .gallery-grid { columns: 3; }
        }
        @media (max-width: 480px) {
          .gallery-img {
            aspect-ratio: 16/9;
            object-fit: cover;
          }
        }
      `}</style>
    </section>
  );
}
